/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Drawer, Divider } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText } from "@octopusdeploy/design-system-octopus-components";
import { cloneDeep } from "lodash";
import React, { useEffect } from "react";
import type { DeepPartial } from "redux";
import DynamicConnectivityCheck from "~/components/DynamicForm/DynamicConnectivityCheck";
import { ExpandableFormSection, Sensitive, Summary } from "~/components/form";
import FormSectionHeading from "~/components/form/Sections/FormSectionHeading";
import type { JiraServiceManagementIntegrationConnection } from "./JiraServiceManagementIntegrationSettingsPage";
export interface JiraServiceManagementIntegrationDrawerProps {
    connection: JiraServiceManagementIntegrationConnection | undefined;
    connectivityTestUrl: string | undefined;
    isOpen: boolean;
    isEdit: boolean;
    isDisabled?: boolean;
    onClose: () => void;
    onSuccess: (result: JiraServiceManagementIntegrationConnection) => void;
}
interface JiraServiceManagementConnectivitySettings {
    BaseUrl?: string;
    Id?: string;
    Token?: string;
    Username?: string;
}
const emptyConnection: JiraServiceManagementIntegrationConnection = {
    Id: undefined,
    ConnectionName: undefined,
    BaseUrl: undefined,
    Username: undefined,
    Token: {
        HasValue: false,
        NewValue: undefined,
    },
};
export function JiraServiceManagementIntegrationDrawer({ connection, connectivityTestUrl, isOpen, isEdit, isDisabled, onClose, onSuccess }: JiraServiceManagementIntegrationDrawerProps) {
    const [state, setState] = React.useState<JiraServiceManagementIntegrationConnection>(() => cloneDeep(connection ?? emptyConnection));
    const [connectivityValues, setConnectivityValues] = React.useState<JiraServiceManagementConnectivitySettings>({});
    useEffect(() => {
        if (isOpen) {
            setState(cloneDeep(connection ?? emptyConnection));
            setConnectivityValues({ Id: connection?.Id, BaseUrl: connection?.BaseUrl, Username: connection?.Username, Token: connection?.Token?.NewValue });
        }
    }, [connection, isOpen]);
    const updateConnection = (updated: DeepPartial<JiraServiceManagementIntegrationConnection>) => {
        setState((prev) => ({
            ...prev,
            ...updated,
            Token: {
                ...prev.Token,
                ...updated.Token,
            },
        }));
        setConnectivityValues((prev) => ({
            Id: updated.Id ?? prev.Id,
            BaseUrl: updated.BaseUrl ?? prev.BaseUrl,
            Username: updated.Username ?? prev.Username,
            Token: updated.Token?.NewValue ?? prev.Token,
        }));
    };
    const onSave = () => {
        onSuccess(state);
    };
    return (<Drawer isOpen={isOpen} onClose={onClose} onPrimaryButtonClick={onSave} isPrimaryButtonDisabled={isDisabled} title={isEdit ? "Edit Connection" : "Add Connection"} variant="default">
            <Divider />
            <FormSectionHeading title="Connection"/>
            <ExpandableFormSection key="ConnectionName" errorKey="ConnectionName" title="Jira Service Management Connection Name" help="Enter a name for this connection." isExpandedByDefault={!isEdit} summary={Summary.summary(state.ConnectionName)}>
                <DebounceText id="ConnectionName" label="Jira Service Management Connection Name" value={state.ConnectionName ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ ConnectionName: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="BaseUrl" errorKey="BaseUrl" title="Jira Service Management Base Url" help="Enter the base url of your Jira Service Management instance." isExpandedByDefault={!isEdit} summary={Summary.summary(state.BaseUrl)}>
                <DebounceText id="BaseUrl" label="Jira Service Management Base Url" value={state.BaseUrl ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ BaseUrl: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="Username" errorKey="Username" title="Jira Service Management Username" help="Enter the username (email address) used to authenticate with Jira Service Management." isExpandedByDefault={!isEdit} summary={Summary.summary(state.Username)}>
                <DebounceText id="Username" label="Jira Service Management Username" value={state.Username ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ Username: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="Token" errorKey="Token" title="Jira Service Management user token" help="Enter the API token for your Jira Service Management account." isExpandedByDefault={!isEdit} summary={Summary.summary(state.Token?.HasValue ? "******" : "No value provided")}>
                <Sensitive label="Jira Service Management user token" value={state.Token} disabled={isDisabled} canRemove={!isDisabled} onChange={(value) => updateConnection({ Token: value })}/>
                {connectivityTestUrl && <DynamicConnectivityCheck title="Verifying Jira Service Management connectivity" url={connectivityTestUrl} values={connectivityValues}/>}
            </ExpandableFormSection>
        </Drawer>);
}
